import React, { useRef, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import mapboxgl from '!mapbox-gl';
import MapboxLanguage from '@mapbox/mapbox-gl-language';

mapboxgl.accessToken =
  'pk.eyJ1IjoiZGVzaWduZW5nIiwiYSI6ImNrbzFoeWw3OTA4dnMycWxwczZ5MHMzMW8ifQ.qVKwBAKVVM8CKMhJKhVN0g';

const loadImageP = (map, imagePath) => {
  return new Promise((resolve, reject) => {
    map.loadImage(imagePath, (error, image) => {
      if (error) {
        reject(error);
      } else {
        resolve(image);
      }
    });
  });
};

function MapContainer({ geoJson, setActiveItem }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const activeMarker = useRef(null);

  var markerToggleCookieValue = Cookies.get('marker-toggle');
  if (!markerToggleCookieValue || isNaN(parseInt(markerToggleCookieValue))) {
    Cookies.set('marker-toggle', 1);
  }

  const addPointsSourceAndLayer = useCallback((map) => {
    map.addSource('points', {
      type: 'geojson',
      data: geoJson,
    });
    map.addLayer({
      id: 'monument',
      type: 'symbol',
      source: 'points',
      layout: {
        'icon-image': 'marker-icon',
        'icon-size': 0.5,
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-offset': [0, 1.25],
        'text-anchor': 'top',
      },
    });
  });

  function ToggleMarkersControl() {
    console.log('ToggleMarkersControl inited');
  }

  ToggleMarkersControl.prototype.onAdd = function (map) {
    this._map = map;
    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    this._container.innerHTML = `
    <button class="" type="button" aria-label="Toggle markers">
      <div class="mapboxgl-ctrl-icon marker-toggle-wrapper" aria-hidden="true" title="Toggle markers">
        <div class="marker-toggle-control" />
      </div>
    </button>`;

    this._container.addEventListener('click', () => {
      markerToggleCookieValue = parseInt(markerToggleCookieValue) + 1;
      Cookies.set('marker-toggle', markerToggleCookieValue);
      window.location = '/';
    });

    return this._container;
  };

  ToggleMarkersControl.prototype.onRemove = function () {
    this._container.parentNode.removeChild(this._container);
    this._map = undefined;
  };

  const lng = 37.61556;
  const lat = 55.75222;
  const zoom = 12;

  useEffect(() => {
    if (map.current) return; // initialize map only once
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/light-v10',
      center: [lng, lat],
      zoom: zoom,
      attributionControl: false,
    });

    map.current.addControl(new mapboxgl.FullscreenControl());
    map.current.addControl(new mapboxgl.NavigationControl());

    if (markerToggleCookieValue % 2) {
      map.current.on('load', () => {
        map.current.loadImage('/marker-icon.png', async (error, image) => {
          if (error) throw error;

          const imageHighlighted = await loadImageP(
            map.current,
            '/marker-icon-highlighted.png',
          );
          map.current.addImage('marker-icon-highlighted', imageHighlighted);
          map.current.addImage('marker-icon', image);

          addPointsSourceAndLayer(map.current);
        });

        map.current.on('click', 'monument', (e) => {
          map.current.flyTo({
            center: e.features[0].geometry.coordinates,
          });
          setActiveItem({
            nid: e.features[0].properties.nid,
          });

          if (map.current.getLayer('monument-highlighted')) {
            map.current.removeLayer('monument-highlighted');
          }

          if (map.current.getSource('points-highlighted')) {
            map.current.removeSource('points-highlighted');
          }

          map.current.addSource('points-highlighted', {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: e.features,
            },
          });

          map.current.addLayer({
            id: 'monument-highlighted',
            type: 'symbol',
            source: 'points-highlighted',
            layout: {
              'icon-image': 'marker-icon-highlighted',
              'icon-size': 0.5,
              'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
              'text-offset': [0, 1.25],
              'text-anchor': 'top',
            },
          });
        });

        const popup = new mapboxgl.Popup({
          closeButton: false,
          closeOnClick: false,
          offset: [0, -12],
        });

        map.current.on('mouseenter', 'monument', (e) => {
          map.current.getCanvas().style.cursor = 'pointer';
          const coordinates = e.features[0].geometry.coordinates.slice();
          const name = e.features[0].properties.name;
          popup.setLngLat(coordinates).setHTML(name).addTo(map.current);
        });

        // Change it back to a pointer when it leaves.
        map.current.on('mouseleave', 'monument', () => {
          map.current.getCanvas().style.cursor = '';
          popup.remove();
        });
      });
    } else {
      const features = geoJson.features;
      for (let index = 0; index < features.length; index++) {
        const feat = features[index];
        const coordinates = feat.geometry.coordinates;
        const el = document.createElement('div');
        el.className = 'marker';
        el.dataset.nid = feat.properties.nid;

        const marker = new mapboxgl.Marker(el)
          .setLngLat(coordinates)
          .addTo(map.current);
        el.addEventListener('click', (e) => {
          var activeMarkerElement = activeMarker.current;

          if (activeMarkerElement) {
            activeMarkerElement.classList.remove('active-marker');
          }

          var nid = e.target.dataset.nid;

          setActiveItem({ nid });

          e.target.classList.add('active-marker');
          activeMarker.current = e.target;
        });
      }
    }

    mapboxgl.setRTLTextPlugin(
      'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.3/mapbox-gl-rtl-text.js',
    );

    map.current.addControl(
      new MapboxLanguage({
        defaultLanguage: 'ru',
      }),
    );

    map.current.addControl(
      new mapboxgl.AttributionControl({
        customAttribution: '© Архнадзор',
      }),
    );

    map.current.addControl(new ToggleMarkersControl());
  }, [geoJson]);

  return <div ref={mapContainer} className="map-container" />;
}

export default MapContainer;
